const CACHE_CONFIG = {
  MEMORY_LIMIT: 100,
  
  TTL: {
    LOGOS: 24 * 60 * 60 * 1000,
    CONTACTS: 15 * 60 * 1000,
    COMPANIES: 30 * 60 * 1000,
    SEARCH_RESULTS: 10 * 60 * 1000,
    METADATA: 60 * 60 * 1000,
    STATIC_DATA: 7 * 24 * 60 * 60 * 1000
  },
  
  STORAGE_KEYS: {
    CACHE_VERSION: 'cache_version',
    LOGO_CACHE: 'logo_cache',
    COMPANY_CACHE: 'company_cache',
    METADATA_CACHE: 'metadata_cache'
  },
  
  // Current cache version (increment to invalidate all caches)
  CURRENT_VERSION: '1.1.0'
};

class CacheManager {
  constructor() {
    this.memoryCache = new Map();
    this.cacheStats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      errors: 0,
      totalSize: 0
    };
    
    this.isSupported = {
      localStorage: this.checkStorageSupport('localStorage'),
      sessionStorage: this.checkStorageSupport('sessionStorage')
    };
    
    this.initializeCacheVersion();
    
    this.setupAutomaticCleanup();
  }

  /**
   * Check if storage is supported and available
   * @param {string} storageType - 'localStorage' or 'sessionStorage'
   * @returns {boolean} True if storage is supported
   */
  checkStorageSupport(storageType) {
    try {
      const storage = window[storageType];
      const testKey = '__cache_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn(`${storageType} not supported:`, e.message);
      return false;
    }
  }

  /**
   * Initialize cache version and clear old caches if needed
   */
  initializeCacheVersion() {
    if (!this.isSupported.localStorage) return;
    
    try {
      const storedVersion = localStorage.getItem(CACHE_CONFIG.STORAGE_KEYS.CACHE_VERSION);
      if (storedVersion !== CACHE_CONFIG.CURRENT_VERSION) {
        console.log('Cache version changed, clearing all caches');
        this.clearAllCaches();
        localStorage.setItem(CACHE_CONFIG.STORAGE_KEYS.CACHE_VERSION, CACHE_CONFIG.CURRENT_VERSION);
      }
    } catch (error) {
      console.error('Error initializing cache version:', error);
    }
  }

  /**
   * Set up automatic cleanup intervals
   */
  setupAutomaticCleanup() {
    setInterval(() => {
      this.cleanupExpiredEntries();
    }, 5 * 60 * 1000);
    
    setInterval(() => {
      this.optimizeCache();
    }, 30 * 60 * 1000);
  }

  /**
   * Generate cache key with optional namespace
   * @param {string} key - Base key
   * @param {string} namespace - Optional namespace
   * @returns {string} Formatted cache key
   */
  generateKey(key, namespace = '') {
    const prefix = namespace ? `${namespace}:` : '';
    return `${prefix}${key}`;
  }

  /**
   * Create cache entry with metadata
   * @param {*} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds
   * @returns {Object} Cache entry
   */
  createCacheEntry(data, ttl) {
    return {
      data,
      timestamp: Date.now(),
      ttl,
      expiresAt: Date.now() + ttl,
      accessCount: 0,
      lastAccessed: Date.now(),
      size: this.estimateSize(data)
    };
  }

  /**
   * Estimate the size of data for cache management
   * @param {*} data - Data to estimate
   * @returns {number} Estimated size in bytes
   */
  estimateSize(data) {
    try {
      return JSON.stringify(data).length;
    } catch (error) {
      return 1000;
    }
  }

  /**
   * Check if cache entry is expired
   * @param {Object} entry - Cache entry
   * @returns {boolean} True if expired
   */
  isExpired(entry) {
    return Date.now() > entry.expiresAt;
  }

  /**
   * Get data from memory cache
   * @param {string} key - Cache key
   * @returns {*} Cached data or null
   */
  getFromMemory(key) {
    const entry = this.memoryCache.get(key);
    if (!entry) {
      this.cacheStats.misses++;
      return null;
    }
    
    if (this.isExpired(entry)) {
      this.memoryCache.delete(key);
      this.cacheStats.misses++;
      this.cacheStats.evictions++;
      return null;
    }
    
    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    
    this.cacheStats.hits++;
    return entry.data;
  }

  /**
   * Set data in memory cache with intelligent eviction
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {number} ttl - Time to live
   */
  setInMemory(key, data, ttl) {
    const entry = this.createCacheEntry(data, ttl);
    
    if (this.memoryCache.size >= CACHE_CONFIG.MEMORY_LIMIT) {
      this.evictLeastUsed();
    }
    
    this.memoryCache.set(key, entry);
    this.cacheStats.totalSize += entry.size;
  }

  /**
   * Evict least recently used items from memory cache
   */
  evictLeastUsed() {
    const entries = Array.from(this.memoryCache.entries());
    
    // Sort by access score (combination of recency and frequency)
    entries.sort((a, b) => {
      const scoreA = a[1].accessCount * Math.exp(-(Date.now() - a[1].lastAccessed) / 3600000);
      const scoreB = b[1].accessCount * Math.exp(-(Date.now() - b[1].lastAccessed) / 3600000);
      return scoreA - scoreB;
    });
    
    const itemsToRemove = Math.ceil(entries.length * 0.2);
    for (let i = 0; i < itemsToRemove; i++) {
      const [key, entry] = entries[i];
      this.memoryCache.delete(key);
      this.cacheStats.totalSize -= entry.size;
      this.cacheStats.evictions++;
    }
  }

  /**
   * Get data from localStorage
   * @param {string} key - Cache key
   * @returns {*} Cached data or null
   */
  getFromLocalStorage(key) {
    if (!this.isSupported.localStorage) return null;
    
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      const entry = JSON.parse(stored);
      if (this.isExpired(entry)) {
        localStorage.removeItem(key);
        return null;
      }
      
      return entry.data;
    } catch (error) {
      this.cacheStats.errors++;
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Set data in localStorage
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {number} ttl - Time to live
   */
  setInLocalStorage(key, data, ttl) {
    if (!this.isSupported.localStorage) return;
    
    try {
      const entry = this.createCacheEntry(data, ttl);
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      this.cacheStats.errors++;
      console.error('Error writing to localStorage:', error);
      
      if (error.name === 'QuotaExceededError') {
        this.cleanupLocalStorage();
        try {
          const entry = this.createCacheEntry(data, ttl);
          localStorage.setItem(key, JSON.stringify(entry));
        } catch (retryError) {
          console.error('Failed to cache after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Clean up expired entries from localStorage
   */
  cleanupLocalStorage() {
    if (!this.isSupported.localStorage) return;
    
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('cache')) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const entry = JSON.parse(stored);
            if (this.isExpired(entry)) {
              keysToRemove.push(key);
            }
          }
        } catch (error) {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Generic get method with multi-level cache checking
   * @param {string} key - Cache key
   * @param {string} namespace - Optional namespace
   * @returns {*} Cached data or null
   */
  get(key, namespace = '') {
    const fullKey = this.generateKey(key, namespace);
    
    let data = this.getFromMemory(fullKey);
    if (data !== null) return data;
    
    data = this.getFromLocalStorage(fullKey);
    if (data !== null) {
      this.setInMemory(fullKey, data, CACHE_CONFIG.TTL.CONTACTS);
      return data;
    }
    
    return null;
  }

  /**
   * Generic set method with intelligent storage selection
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {number} ttl - Time to live
   * @param {string} namespace - Optional namespace
   * @param {boolean} persistToLocal - Whether to persist to localStorage
   */
  set(key, data, ttl, namespace = '', persistToLocal = false) {
    const fullKey = this.generateKey(key, namespace);
    
    this.setInMemory(fullKey, data, ttl);
    
    if (persistToLocal || ttl > 60 * 60 * 1000) {
      this.setInLocalStorage(fullKey, data, ttl);
    }
  }

  /**
   * Remove item from all cache levels
   * @param {string} key - Cache key
   * @param {string} namespace - Optional namespace
   */
  remove(key, namespace = '') {
    const fullKey = this.generateKey(key, namespace);
    
    const memoryEntry = this.memoryCache.get(fullKey);
    if (memoryEntry) {
      this.cacheStats.totalSize -= memoryEntry.size;
      this.memoryCache.delete(fullKey);
    }
    
    if (this.isSupported.localStorage) {
      localStorage.removeItem(fullKey);
    }
  }

  /**
   * Clear all caches
   */
  clearAllCaches() {
    this.memoryCache.clear();
    this.cacheStats.totalSize = 0;
    
    if (this.isSupported.localStorage) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('cache') || Object.values(CACHE_CONFIG.STORAGE_KEYS).includes(key))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  /**
   * Clean up expired entries from all caches
   */
  cleanupExpiredEntries() {
    const memoryKeysToRemove = [];
    for (const [key, entry] of this.memoryCache.entries()) {
      if (this.isExpired(entry)) {
        memoryKeysToRemove.push(key);
        this.cacheStats.totalSize -= entry.size;
      }
    }
    memoryKeysToRemove.forEach(key => this.memoryCache.delete(key));
    
    this.cleanupLocalStorage();
  }

  /**
   * Optimize cache performance
   */
  optimizeCache() {
    // If memory usage is high, be more aggressive with eviction
    if (this.cacheStats.totalSize > 10 * 1024 * 1024) {
      const targetSize = 5 * 1024 * 1024;
      const entries = Array.from(this.memoryCache.entries());
      
      entries.sort((a, b) => {
        const scoreA = a[1].size / (a[1].accessCount + 1);
        const scoreB = b[1].size / (b[1].accessCount + 1);
        return scoreB - scoreA;
      });
      
      let currentSize = this.cacheStats.totalSize;
      for (const [key, entry] of entries) {
        if (currentSize <= targetSize) break;
        
        this.memoryCache.delete(key);
        currentSize -= entry.size;
        this.cacheStats.evictions++;
      }
      
      this.cacheStats.totalSize = currentSize;
    }
  }

  /**
   * Get cache statistics and performance metrics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const hitRate = this.cacheStats.hits + this.cacheStats.misses > 0 
      ? (this.cacheStats.hits / (this.cacheStats.hits + this.cacheStats.misses)) * 100 
      : 0;
    
    return {
      ...this.cacheStats,
      hitRate: Math.round(hitRate * 100) / 100,
      memorySize: this.memoryCache.size,
      estimatedMemoryUsage: Math.round(this.cacheStats.totalSize / 1024),
      isOptimal: hitRate > 70 && this.cacheStats.errors < 10,
      recommendations: this.getOptimizationRecommendations(hitRate)
    };
  }

  /**
   * Get optimization recommendations based on cache performance
   * @param {number} hitRate - Current cache hit rate
   * @returns {Array} Array of recommendation strings
   */
  getOptimizationRecommendations(hitRate) {
    const recommendations = [];
    
    if (hitRate < 50) {
      recommendations.push('Consider increasing cache TTL for frequently accessed data');
    }
    
    if (this.cacheStats.evictions > this.cacheStats.hits * 0.1) {
      recommendations.push('Cache is evicting too frequently - consider increasing memory limit');
    }
    
    if (this.cacheStats.errors > 10) {
      recommendations.push('High error rate detected - check storage availability');
    }
    
    if (this.cacheStats.totalSize > 20 * 1024 * 1024) {
      recommendations.push('Memory usage is high - consider reducing cache size');
    }
    
    return recommendations;
  }
}

export class LogoCacheStrategy {
  constructor(cacheManager) {
    this.cache = cacheManager;
    this.namespace = 'logos';
  }

  get(logoUuid) {
    return this.cache.get(logoUuid, this.namespace);
  }

  set(logoUuid, logoData) {
    this.cache.set(logoUuid, logoData, CACHE_CONFIG.TTL.LOGOS, this.namespace, true);
  }

  remove(logoUuid) {
    this.cache.remove(logoUuid, this.namespace);
  }

  preloadLogos(logoUuids) {
    // Implementation for logo preloading would go here
    console.log('Preloading logos:', logoUuids);
  }
}

export class SearchCacheStrategy {
  constructor(cacheManager) {
    this.cache = cacheManager;
    this.namespace = 'search';
  }

  generateSearchKey(query, filters = {}) {
    const searchParams = { query, ...filters };
    return btoa(JSON.stringify(searchParams)).replace(/[^a-zA-Z0-9]/g, '');
  }

  get(query, filters = {}) {
    const key = this.generateSearchKey(query, filters);
    return this.cache.get(key, this.namespace);
  }

  set(query, results, filters = {}) {
    const key = this.generateSearchKey(query, filters);
    this.cache.set(key, results, CACHE_CONFIG.TTL.SEARCH_RESULTS, this.namespace);
  }

  clear() {
    // Clear all search cache - implementation would iterate through search keys
    console.log('Clearing search cache');
  }
}

export class CompanyCacheStrategy {
  constructor(cacheManager) {
    this.cache = cacheManager;
    this.namespace = 'companies';
  }

  get(companyId) {
    return this.cache.get(companyId, this.namespace);
  }

  set(companyId, companyData) {
    this.cache.set(companyId, companyData, CACHE_CONFIG.TTL.COMPANIES, this.namespace, true);
  }

  remove(companyId) {
    this.cache.remove(companyId, this.namespace);
  }

  bulkSet(companies) {
    companies.forEach(company => {
      if (company.id) {
        this.set(company.id, company);
      }
    });
  }
}

const mainCacheManager = new CacheManager();

export const logoCache = new LogoCacheStrategy(mainCacheManager);
export const searchCache = new SearchCacheStrategy(mainCacheManager);
export const companyCache = new CompanyCacheStrategy(mainCacheManager);

export default mainCacheManager;

export const cacheUtils = {
  clear: () => mainCacheManager.clearAllCaches(),
  stats: () => mainCacheManager.getStats(),
  cleanup: () => mainCacheManager.cleanupExpiredEntries(),
  optimize: () => mainCacheManager.optimizeCache()
};
